import { useMemo, useState, type ReactNode } from 'react'
import dayjs from 'dayjs'
import {
  useAdvisoryAiOrgDetails,
  useAdvisoryAiOrgReports,
  useAdvisoryAiOrgs,
  useConsolidatedAnalysisList,
  useAdvisoryAiReport,
} from '@/features/advisory-ai/hooks'
import { useAuthSession } from '@/lib/session'
import { formatDateTime, platformRefreshStatusTagColor } from '../utils'
import { reportStatusRow } from '../consolidated/utils'
import { isSampleCall } from '../sampleCall'
import {
  getCallStatus,
  getDaysAway,
  getMeetingDateDisplay,
  getStepProgress,
  isRealMeetingDate,
} from '../calls/utils'
import { AssistantProvider } from './AssistantProvider'
import { PreppyDock } from './PreppyDock'
import { PopupBubble } from './PopupBubble'
import { AssistantPanel, ASSISTANT_PANEL_WIDTH } from './AssistantPanel'
import { WalkthroughOverlay } from './walkthrough/WalkthroughOverlay'
import { useAdvicePrepScope } from './useAdvicePrepScope'
import type { AssistantSuggestion, AssistantSuggestions } from './types'

/**
 * Mounts Preppy once for the whole of AdvicePrep, above the router outlet.
 *
 * One instance rather than one per page, for two reasons. Six copies of the
 * same state is six chances for them to disagree, and — more usefully — a
 * half-written support ticket or an unread pop-up now survives navigating from
 * the organisation list into a prep and back out again.
 *
 * It also feeds the assistant whatever the current screen can offer: the Xero
 * organisations on the list, the meetings inside one. Both queries reuse the
 * page's own query keys, so they are cache reads rather than extra requests.
 *
 * Outside AdvicePrep this renders its children and nothing else.
 */

/** Same filter the organisation list uses, so the query key matches. */
const XERO_ACCOUNTING_TYPE_ID = 1

/**
 * Most urgent band first, so the first thing Preppy offers is the thing that needs
 * work. Generating sits second-last: it is further along than an unfinished prep,
 * and there is nothing to do but wait.
 */
const STATUS_RANK = { needsAttention: 0, inProgress: 1, generating: 2, ready: 3 } as const

const MEETING_TAG = {
  needsAttention: { label: 'Needs attention', colour: 'red' },
  inProgress: { label: 'In progress', colour: 'orange' },
  generating: { label: 'Generating outputs', colour: 'blue' },
  ready: { label: 'Ready', colour: 'green' },
} as const

const MAX_SUGGESTIONS = 6

const NO_SUGGESTIONS: AssistantSuggestions = { items: [], loading: false, intro: '', note: '' }

type AdvicePrepAssistantHostProps = {
  children: ReactNode
  /** Asks the layout to bring the sidebar back, for walkthrough steps that point at it. */
  onRevealNav?: (navKey?: string) => void
}

export function AdvicePrepAssistantHost({ children, onRevealNav }: AdvicePrepAssistantHostProps) {
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''
  const location = useAdvicePrepScope()
  const [open, setOpen] = useState(false)

  const scope = location?.scope
  const orgId = location?.orgId ?? ''
  const callId = location?.callId ?? ''

  // Every query below is `enabled`-guarded on an id that is only non-empty on
  // the screen that needs it, so nothing fetches speculatively. Polling is left
  // to the pages (shouldPoll defaults false) so we never double up on it.
  const orgQuery = useAdvisoryAiOrgDetails(orgId, clientId)
  const reportQuery = useAdvisoryAiReport(callId, clientId)

  // Organisation list: the same unfiltered Xero query the page opens with, so
  // this is a cache read. Preppy deliberately keeps showing every organisation
  // even once you start typing in the page's own filter.
  const orgsQuery = useAdvisoryAiOrgs({
    clientId: scope === 'org-list' ? clientId : '',
    companyName: '',
    typeId: XERO_ACCOUNTING_TYPE_ID,
  })

  // Consolidated groups: only so a walkthrough has a real group to open. Also
  // fetched on the organisation list, because the consolidated guide can be
  // started from there and its last step needs a group id before it runs.
  const groupsQuery = useConsolidatedAnalysisList(
    clientId,
    scope === 'consolidated' || scope === 'org-list'
  )

  // Meetings list: same key as the page's own call, capped when we map it.
  const callsQuery = useAdvisoryAiOrgReports(scope === 'calls' ? orgId : '', clientId, 1, 1000)

  const orgSuggestions = useMemo<AssistantSuggestion[]>(() => {
    const rows = orgsQuery.data
    if (!rows?.length) return []

    return rows.slice(0, MAX_SUGGESTIONS).map((row) => ({
      id: row.id,
      title: row.companyName || row.name,
      detail: row.lastSuccessfulRefresh
        ? `${row.platformTypeLabel} · refreshed ${formatDateTime(row.lastSuccessfulRefresh)}`
        : `${row.platformTypeLabel} · never refreshed`,
      tag: {
        label: row.refreshStatus,
        colour: platformRefreshStatusTagColor(row.refreshStatus),
      },
      to: `/adviceprep/org/${row.companyId}/calls`,
    }))
  }, [orgsQuery.data])

  const meetingSuggestions = useMemo<AssistantSuggestion[]>(() => {
    const rows = callsQuery.data?.rows
    if (!rows?.length || !orgId) return []

    return rows
      .map((row) => {
        const status = getCallStatus(row)
        const when = isRealMeetingDate(row.meetingDate) ? getDaysAway(row.meetingDate) : ''
        const progress = getStepProgress(row).label
        return {
          id: row.advisoryCallId,
          title: getMeetingDateDisplay(row.meetingDate),
          detail: when ? `${progress} · ${when}` : progress,
          tag: MEETING_TAG[status],
          to: `/adviceprep/org/${orgId}/report/${row.advisoryCallId}`,
          // Kept only for ordering — most recent first inside a band, which is
          // how the meetings list underneath sorts them.
          rank: STATUS_RANK[status],
          sortAt: dayjs(row.meetingDate ?? row.startDate).valueOf() || 0,
        }
      })
      .sort((a, b) => a.rank - b.rank || b.sortAt - a.sortAt)
      .slice(0, MAX_SUGGESTIONS)
      .map(({ rank: _rank, sortAt: _sortAt, ...suggestion }) => suggestion)
  }, [callsQuery.data?.rows, orgId])

  const consolidatedSuggestions = useMemo<AssistantSuggestion[]>(() => {
    const rows = groupsQuery.data
    if (!rows?.length) return []

    return rows
      .map((row) => {
        const statusRow = reportStatusRow(row)
        const status = getCallStatus(statusRow)
        const when = isRealMeetingDate(row.meetingDate) ? getDaysAway(row.meetingDate) : ''
        const orgCount = row.orgIds.length
        return {
          id: row.advisoryCallId,
          title: row.meetingTitle || 'Consolidated analysis',
          detail: [
            getStepProgress(statusRow).label,
            orgCount ? `${orgCount} organisations` : '',
            when,
          ]
            .filter(Boolean)
            .join(' · '),
          tag: MEETING_TAG[status],
          to: `/adviceprep/consolidated/${row.advisoryCallId}`,
          rank: STATUS_RANK[status],
          sortAt: dayjs(row.meetingDate ?? row.createdAt).valueOf() || 0,
        }
      })
      .sort((a, b) => a.rank - b.rank || b.sortAt - a.sortAt)
      .slice(0, MAX_SUGGESTIONS)
      .map(({ rank: _rank, sortAt: _sortAt, ...suggestion }) => suggestion)
  }, [groupsQuery.data])

  /**
   * Never let the cap pass as "that is all of them" — a list that quietly stops
   * at six reads as a complete list, and the page behind is the one with the
   * search box.
   */
  const truncationNote = (shown: number, total: number, noun: string) =>
    total > shown ? `Showing ${shown} of ${total} ${noun}; the full list and its search are on the page behind me. ` : ''

  const suggestions = useMemo<AssistantSuggestions>(() => {
    if (scope === 'org-list') {
      const total = orgsQuery.data?.length ?? 0
      return {
        items: orgSuggestions,
        loading: orgsQuery.isLoading,
        intro:
          'These are the Xero organisations you can prep for. Pick one and I will take you to its meetings.',
        note:
          truncationNote(orgSuggestions.length, total, 'organisations') +
          'Anything not marked refreshed is working from older data, which will show up in the analysis.',
      }
    }
    if (scope === 'calls') {
      const total = callsQuery.data?.rows?.length ?? 0
      return {
        items: meetingSuggestions,
        loading: callsQuery.isLoading,
        intro:
          'I read one meeting at a time. Here is what needs you most — pick one and I will open it and read the analysis.',
        note:
          truncationNote(meetingSuggestions.length, total, 'meetings') +
          'Ordered by what needs work, not by date. Opening one takes you into its prep — you will not lose anything you have typed in here.',
      }
    }
    if (scope === 'consolidated') {
      const total = groupsQuery.data?.length ?? 0
      return {
        items: consolidatedSuggestions,
        loading: groupsQuery.isLoading,
        intro:
          'I read one analysis at a time. Here is what needs you most — pick one and I will open it and read it with you.',
        note:
          truncationNote(consolidatedSuggestions.length, total, 'consolidated analyses') +
          'Ordered by what needs work, not by date. Each one covers several organisations at once.',
      }
    }
    return NO_SUGGESTIONS
  }, [
    callsQuery.data?.rows?.length,
    callsQuery.isLoading,
    consolidatedSuggestions,
    groupsQuery.data?.length,
    groupsQuery.isLoading,
    meetingSuggestions,
    orgSuggestions,
    orgsQuery.data?.length,
    orgsQuery.isLoading,
    scope,
  ])

  /**
   * What `:orgId` / `:callId` resolve to in a walkthrough route.
   *
   * Whatever is open wins; failing that, the first organisation and meeting the
   * user actually has. That is what lets the app tour run for someone sitting on
   * the organisation list who has never opened a meeting — and what lets it skip
   * those steps cleanly for someone who has none at all.
   */
  const walkParams = useMemo(
    () => ({
      orgId: orgId || orgsQuery.data?.[0]?.companyId || '',
      // A consolidated analysis IS an advisory call, so its groupId fills the
      // same slot — which is what lets the consolidated walkthrough open one
      // from the list page, where nothing is open yet.
      callId:
        callId ||
        callsQuery.data?.rows?.[0]?.advisoryCallId ||
        groupsQuery.data?.[0]?.advisoryCallId ||
        '',
    }),
    [callId, callsQuery.data?.rows, groupsQuery.data, orgId, orgsQuery.data]
  )

  if (!location) return <>{children}</>

  return (
    <AssistantProvider
      open={open}
      onOpenChange={setOpen}
      scope={location.scope}
      clientId={clientId}
      callId={callId || undefined}
      orgId={orgId}
      orgName={orgQuery.data?.name}
      reportStatus={reportQuery.data?.status}
      suggestions={suggestions}
      walkParams={walkParams}
      hasMeeting={
        callId
          ? !isSampleCall(callId)
          : callsQuery.isSuccess
            ? Boolean(callsQuery.data?.rows?.length)
            : undefined
      }
      onRevealNav={onRevealNav}
    >
      <div
        className="transition-[margin] duration-300 ease-in-out"
        style={{ marginRight: open ? ASSISTANT_PANEL_WIDTH : 0 }}
      >
        {children}
      </div>
      <PreppyDock />
      <PopupBubble />
      <AssistantPanel />
      <WalkthroughOverlay />
    </AssistantProvider>
  )
}
